import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, CheckCircle2, ArrowLeft, Keyboard } from "lucide-react";
import POPhotos from "@/components/POPhotos";

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500";
const blankLine = () => ({ key: Math.random().toString(36).slice(2), sku: "", barcode: "", product_title: "", expected_qty: "", customer_location: "", notes: "" });

/** Type a small PO straight in — no spreadsheet needed. Built to work on a phone. */
export default function POManualEntry({ accountId, onBack, onCreated }) {
  const [meta, setMeta] = useState({ po_name: "", customer_po_number: "", vendor: "", tracking_number: "", expected_arrival: "", notes: "" });
  const [lines, setLines] = useState([blankLine()]);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const setLine = (i, k, v) => setLines((arr) => arr.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((arr) => [...arr, blankLine()]);
  const removeLine = (i) => setLines((arr) => (arr.length === 1 ? [blankLine()] : arr.filter((_, idx) => idx !== i)));

  const filled = lines.filter((l) => (l.sku || "").trim() || (l.product_title || "").trim());
  const totalUnits = filled.reduce((n, l) => n + (parseInt(l.expected_qty, 10) || 0), 0);

  const submit = async () => {
    if (!filled.length) { toast.error("Add at least one product line"); return; }
    if (filled.some((l) => !(parseInt(l.expected_qty, 10) > 0))) { toast.error("Every line needs a quantity greater than 0"); return; }
    setBusy(true);
    try {
      await api.post("/inventory/pos", {
        account_id: accountId,
        ...meta,
        photos: photos.map((p) => ({ id: p.id, storage_path: p.storage_path })),
        lines: filled.map((l) => ({
          sku: l.sku.trim(),
          barcode: (l.barcode || "").trim(),
          product_title: l.product_title.trim(),
          expected_qty: parseInt(l.expected_qty, 10),
          customer_location: (l.customer_location || "").trim(),
          notes: (l.notes || "").trim(),
        })),
        submit: true,
      });
      toast.success(`PO submitted — ${filled.length} product${filled.length === 1 ? "" : "s"}, ${totalUnits} units`);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="po-manual-entry">
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
        <Keyboard className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900">
          Punch in what you&apos;re sending us — one line per product with the quantity you&apos;re shipping.
          Add the barcode if you have it — it lets us scan the stock straight in.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Labelled label="PO name" hint="how you'll recognise it">
          <input data-testid="po-manual-name" value={meta.po_name} onChange={(e) => setMeta((m) => ({ ...m, po_name: e.target.value }))} placeholder="March restock" className={inputCls} />
        </Labelled>
        <Labelled label="Your PO number">
          <input data-testid="po-manual-number" value={meta.customer_po_number} onChange={(e) => setMeta((m) => ({ ...m, customer_po_number: e.target.value }))} placeholder="PO-1042" className={inputCls} />
        </Labelled>
        <Labelled label="Tracking number" hint="if it's already shipped">
          <input data-testid="po-manual-tracking" value={meta.tracking_number} onChange={(e) => setMeta((m) => ({ ...m, tracking_number: e.target.value }))} placeholder="1Z…" className={inputCls} />
        </Labelled>
        <Labelled label="Supplier / vendor">
          <input data-testid="po-manual-vendor" value={meta.vendor} onChange={(e) => setMeta((m) => ({ ...m, vendor: e.target.value }))} className={inputCls} />
        </Labelled>
        <Labelled label="Expected arrival">
          <input data-testid="po-manual-arrival" type="date" value={meta.expected_arrival} onChange={(e) => setMeta((m) => ({ ...m, expected_arrival: e.target.value }))} className={inputCls} />
        </Labelled>
        <Labelled label="Notes for the warehouse">
          <input data-testid="po-manual-notes" value={meta.notes} onChange={(e) => setMeta((m) => ({ ...m, notes: e.target.value }))} placeholder="Fragile — 2 pallets" className={inputCls} />
        </Labelled>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-slate-800">What&apos;s in this PO</h3>
          <span className="text-xs text-slate-500" data-testid="po-manual-total">
            {filled.length} product{filled.length === 1 ? "" : "s"} · {totalUnits} units
          </span>
        </div>

        <div className="hidden md:block border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-2 px-3">SKU</th>
                <th className="py-2 px-3">Barcode</th>
                <th className="py-2 px-3">Product name</th>
                <th className="py-2 px-3 w-24">Qty</th>
                <th className="py-2 px-3 w-32">Location</th>
                <th className="py-2 px-3">Line note</th>
                <th className="py-2 px-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.key} className="border-t border-slate-100" data-testid={`po-manual-row-${i}`}>
                  <td className="py-1.5 px-3"><input data-testid={`po-manual-sku-${i}`} value={l.sku} onChange={(e) => setLine(i, "sku", e.target.value)} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm font-mono" /></td>
                  <td className="py-1.5 px-3"><input data-testid={`po-manual-barcode-${i}`} value={l.barcode} onChange={(e) => setLine(i, "barcode", e.target.value)} placeholder="UPC" className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm font-mono" /></td>
                  <td className="py-1.5 px-3"><input data-testid={`po-manual-title-${i}`} value={l.product_title} onChange={(e) => setLine(i, "product_title", e.target.value)} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" /></td>
                  <td className="py-1.5 px-3"><input data-testid={`po-manual-qty-${i}`} type="number" min="1" inputMode="numeric" value={l.expected_qty} onChange={(e) => setLine(i, "expected_qty", e.target.value)} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" /></td>
                  <td className="py-1.5 px-3"><input data-testid={`po-manual-loc-${i}`} value={l.customer_location} onChange={(e) => setLine(i, "customer_location", e.target.value)} placeholder="optional" className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm font-mono" /></td>
                  <td className="py-1.5 px-3"><input data-testid={`po-manual-note-${i}`} value={l.notes} onChange={(e) => setLine(i, "notes", e.target.value)} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" /></td>
                  <td className="py-1.5 px-3 text-right">
                    <button onClick={() => removeLine(i)} data-testid={`po-manual-remove-${i}`} className="text-slate-300 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-3">
          {lines.map((l, i) => (
            <div key={l.key} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`po-manual-card-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-400">Product {i + 1}</span>
                <button onClick={() => removeLine(i)} className="text-slate-300 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              <input value={l.sku} onChange={(e) => setLine(i, "sku", e.target.value)} placeholder="SKU" className={inputCls + " font-mono"} />
              <input value={l.barcode} onChange={(e) => setLine(i, "barcode", e.target.value)} placeholder="Barcode / UPC (helps us scan it in)" className={inputCls + " font-mono"} />
              <input value={l.product_title} onChange={(e) => setLine(i, "product_title", e.target.value)} placeholder="Product name" className={inputCls} />
              <input type="number" min="1" inputMode="numeric" value={l.expected_qty} onChange={(e) => setLine(i, "expected_qty", e.target.value)} placeholder="How many units" className={inputCls} />
            </div>
          ))}
        </div>

        <button onClick={addLine} data-testid="po-manual-add-line" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700">
          <Plus className="w-4 h-4" /> Add another product
        </button>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <h3 className="text-sm font-bold text-slate-800 mb-2">Photos <span className="font-normal text-slate-400">· optional, up to 5</span></h3>
        <POPhotos accountId={accountId} kind="inbound" photos={photos} onChange={setPhotos} />
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <button onClick={onBack} data-testid="po-manual-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={submit} disabled={busy} data-testid="po-manual-submit" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-md transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit to BoxHero
        </button>
      </div>
    </div>
  );
}

function Labelled({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">
        {label}{hint && <span className="normal-case tracking-normal text-slate-400"> · {hint}</span>}
      </label>
      {children}
    </div>
  );
}
